import React, { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import Dashboard from './components/pages/Dashboard';
import AdminPanel from './components/pages/AdminPanel';
import VersionInfo from './components/page_components/VersionInfo';
import useLanguage from './hooks/useLanguage';
import LogoCompany from './assets/LogoCompany';
import { authAPI, userAPI } from './services/api';
import { YearStatusProvider } from './contexts/YearStatusContext';

function App() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [vacationBalance, setVacationBalance] = useState(null);
  const [vacationBalanceForYear, setVacationBalanceForYear] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' or 'admin'

  // Translation support
  const { t, currentLanguage, setCurrentLanguage } = useLanguage();

  // Function to fetch vacation balance
  const fetchVacationBalance = async () => {
    try {
      const vacationBalance = await userAPI.getVacationBalance();
      setVacationBalance(vacationBalance);
    } catch (error) {
      console.error("Error fetching vacation balance:", error);
    }
  };

  const fetchVacationBalanceForYear = useCallback(async (year) => {
    try {
      const vacationBalanceForYear = await userAPI.getVacationBalanceForYear(year);
      setVacationBalanceForYear(vacationBalanceForYear);
    } catch (error) {
      console.error("Error fetching vacation balance for year:", error);
    }
  }, []);

  // Check if user is already logged in on component mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const user = await userAPI.getCurrentUserInfo();
        if (user) {
          setCurrentUser(user);
          setCurrentUserRole(user.role);
          setIsLoggedIn(true);

          // Fetch vacation balance asynchronously
          await fetchVacationBalance();
          // TODO: check is this can be removed completely - looks like it is not used
          //await fetchVacationBalanceForYear();
        }
      } catch (e) {
        // Not authenticated; ignore
      }
    };

    loadUserData();
  }, [fetchVacationBalanceForYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await authAPI.login(username, password);
      // After storing token, fetch authoritative user info from backend
      const me = await userAPI.getCurrentUserInfo();
      setCurrentUser(me);
      setCurrentUserRole(me.role);
      setIsLoggedIn(true);

      // Fetch vacation balance after successful login
      await fetchVacationBalance();
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setVacationBalance(null);
    setCurrentUserRole(null);
    setUsername('');
    setPassword('');
    setError('');
  };

  // If logged in, show dashboard with admin navigation
  if (isLoggedIn) {
    // Render current view
    if (currentView === 'admin' && currentUserRole === 'admin') {
      return (
        <YearStatusProvider>
          <AdminPanel
            currentUser={currentUser}
            onBackToDashboard={() => setCurrentView('dashboard')}
            currentLanguage={currentLanguage}
            setCurrentLanguage={setCurrentLanguage}
            onLogout={handleLogout}
          />
          <VersionInfo />
        </YearStatusProvider>
      );
    } else {
      return (
        <YearStatusProvider>
          <Dashboard
            currentUser={currentUser}
            vacationBalance={vacationBalance}
            onLogout={handleLogout}
            currentUserRole={currentUserRole}
            currentView={currentView}
            setCurrentView={setCurrentView}
            currentLanguage={currentLanguage}
            setCurrentLanguage={setCurrentLanguage}
            t={t}
            fetchVacationBalance={fetchVacationBalance}
            fetchVacationBalanceForYear={fetchVacationBalanceForYear}
            vacationBalanceForYear={vacationBalanceForYear}
          />
          <VersionInfo />
        </YearStatusProvider>
      );
    }
  }

  // Otherwise show login form
  return (
    <YearStatusProvider>
      <div className="login-container">
        {/* Logo */}
        <div className="logo">
          <div className="logo-icon">
            <LogoCompany size={48} color="#FF4757" />
          </div>
          <span className="logo-text">{t('appName')}</span>
        </div>

        {/* Header */}
        <div className="login-header">
          <p className="login-subtitle">{t('signInSubtitle')}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              {t('username')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="username"
                type="text"
                className="form-input"
                placeholder={t('usernamePlaceholder')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                style={{ paddingLeft: '44px' }}
              />
              <User
                size={20}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('password')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder={t('enterPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ paddingLeft: '44px', paddingRight: '44px' }}
              />
              <Lock
                size={20}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af'
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? t('signingIn') : t('signIn')}
          </button>
        </form>

      </div>
    </YearStatusProvider>
  );
}

export default App;
